import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr } from "../../lib/api";
import { Plus, Ship, Trash2, MailCheck, Clock, XCircle, Pencil, Crown, X, Check, ChevronsUpDown, ArrowRightLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader } from "./Dashboard";
import { COUNTRIES, countryByName } from "../../lib/countries";
import { YACHT_BRANDS, ENGINE_BRANDS, GENERATOR_BRANDS, STABILIZER_BRANDS } from "../../lib/brands";
import { YACHT_MODEL_CATALOG, defaultSpecCounts, typicalEngine, TENDER_BRANDS, TENDER_MODEL_CATALOG } from "../../lib/yachtModelCatalog";
import { ENGINE_CATALOG } from "../../lib/engineCatalog";
import { GENERATOR_CATALOG } from "../../lib/generatorCatalog";
import { STABILIZER_CATALOG } from "../../lib/stabilizerCatalog";
import { ENGINE_LOCATIONS, defaultLocationLayout } from "../../lib/engineLocations";
import ImageUpload from "../../components/ImageUpload";
import YachtDetailsDialog from "../../components/YachtDetailsDialog";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";
import { toast } from "sonner";

// Years offered in the Year dropdown — from 1950 up to next year, descending.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR + 1 - 1950 + 1 },
  (_, i) => CURRENT_YEAR + 1 - i,
);

const DEFAULT = {
  name: "", brand: "", model: "", year: "", length_m: "", flag: "",
  registration_no: "", hin: "", mmsi: "", call_sign: "", home_marina: "", currency: "",
  engines: [],     // array of { brand, type, serial_number, kw, hours }
  generators: [],  // array of { brand, type, serial_number, kw, hours }
  stabilizers: [], // array of { brand, type, serial_number, kw, hours, group }
  // Each tender carries its own identity + an engines[] sub-list. Lets a
  // yacht with several tenders track them as distinct craft instead of one
  // mixed engine pile.
  tenders: [],     // array of { name, brand, model, length_m, cover_image, engines: [...] }
  cover_image: "", notes: "",
  your_role: "",
};

const EMPTY_ENGINE = { id: "", brand: "", type: "", serial_number: "", kw: "", hours: "", group: "", location: "" };
const EMPTY_GENERATOR = { id: "", brand: "", type: "", serial_number: "", kw: "", hours: "", group: "", location: "" };
const EMPTY_STABILIZER = { id: "", brand: "", type: "", serial_number: "", kw: "", hours: "", group: "", location: "" };
const EMPTY_TENDER = { name: "", brand: "", model: "", length_m: "", cover_image: "", engines: [] };

const CAN_DELETE_ROLES = ["owner", "manager", "captain", "first_officer"];

const ROLE_LABELS = {
  owner: "Owner",
  manager: "Manager",
  captain: "Captain",
  first_officer: "First Officer",
  engineer: "Engineer",
  accountant: "Accountant",
  deckhand: "Deckhand",
  stewardess: "Stewardess",
  chef: "Chef",
  hostess: "Hostess",
  cleaner: "Cleaner",
  detailer: "Detailer",
  delivery_crew: "Delivery Crew",
  night_watch: "Night Watch",
  maintenance_helper: "Maintenance Helper",
  provider: "Supplier",
};
const roleLabel = (r) => ROLE_LABELS[r] || r || "member";

export default function Yachts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { yachts, refresh, switchYacht, activeYacht } = useYacht();
  const { user } = useAuth();
  // Yacht creation is reserved for senior account-level roles. The matrix
  // is per-yacht, but creating a brand-new yacht has no existing yacht to
  // check rights against, so we fall back to user.role (which is set on
  // sign-up and reflects their seniority on the first yacht they joined).
  // Backend enforces the same rule via the `your_role` field on POST /yachts.
  const SENIOR_ROLES = ["owner", "manager", "captain"];
  const canCreate = !!user && SENIOR_ROLES.includes((user.role || "").toLowerCase());
  const [open, setOpen] = useState(false);
  // While a guided tour is running, the create dialog must not close when
  // the user clicks the tour tooltip (which lives outside the dialog).
  const { activeTour } = useTour();
  // editingId: null = create mode, otherwise the yacht.id being edited
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);

  // Delete flow state
  const [delTarget, setDelTarget] = useState(null); // { id, name }
  // Read-only details modal shown when the user clicks "Details" on a card.
  // Renders the full spec (engines / generators / tenders / cover photo)
  // without exposing edit controls — non-seniors get a way to view what's
  // on a yacht too.
  const [detailsYacht, setDetailsYacht] = useState(null);
  // step: "confirm" | "otp" | "areYouSure" | "sentToApprover"
  const [delStep, setDelStep] = useState("confirm");
  const [delMode, setDelMode] = useState(null); // "self_otp" | "self_direct" | "approver_link"
  const [delRequestId, setDelRequestId] = useState(null);
  const [delApprover, setDelApprover] = useState(null);
  const [delOtp, setDelOtp] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  // Pending requests indexed by yacht_id
  const [pendingByYacht, setPendingByYacht] = useState({});

  // Ownership-transfer notifications shown as dismissable banners.
  const [notifs, setNotifs] = useState([]);
  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs((data || []).filter((n) => n.type === "ownership_transferred"));
    } catch { /* non-fatal */ }
  };
  useEffect(() => { loadNotifs(); /* eslint-disable-next-line */ }, [user?.id, yachts.length]);

  const dismissNotif = async (n) => {
    setNotifs((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.post(`/notifications/${n.id}/dismiss`); } catch { /* swallow */ }
  };

  // Supplier per-yacht summary (contact + open tasks/quote counts).
  const [supSummary, setSupSummary] = useState({});
  useEffect(() => {
    if (!user?.is_supplier) return;
    (async () => {
      try {
        const { data } = await api.get("/supplier/yacht-summary");
        setSupSummary(Object.fromEntries((data || []).map((r) => [r.yacht_id, r])));
      } catch { /* non-fatal */ }
    })();
  }, [user?.is_supplier, yachts.length]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Snapshot of what the model-pick auto-prefilled, per section. When the
  // user re-picks a model, untouched prefilled rows are replaced with the new
  // model's config; user-edited rows are never clobbered.
  const prefillRef = useRef({ engines: null, generators: null, stabilizers: null });
  const resetPrefillRef = () => { prefillRef.current = { engines: null, generators: null, stabilizers: null }; };

  const openCreate = () => {
    setEditingId(null);
    setForm(DEFAULT);
    resetPrefillRef();
    setOpen(true);
  };

  const openEdit = (y) => {
    setEditingId(y.id);
    resetPrefillRef();
    // Hydrate form from the yacht record, normalising nulls/undefined.
    const next = { ...DEFAULT };
    Object.keys(DEFAULT).forEach((k) => {
      const v = y[k];
      if (k === "engines" || k === "generators" || k === "stabilizers") {
        next[k] = Array.isArray(v)
          ? v.map((it) => ({
              ...EMPTY_ENGINE,
              ...it,
              kw: it?.kw == null ? "" : String(it.kw),
              hours: it?.hours == null ? "" : String(it.hours),
              serial_number: it?.serial_number || "",
            }))
          : [];
      } else if (k === "tenders") {
        next[k] = Array.isArray(v)
          ? v.map((t) => ({
              ...EMPTY_TENDER,
              ...t,
              length_m: t?.length_m == null ? "" : String(t.length_m),
              engines: Array.isArray(t?.engines)
                ? t.engines.map((it) => ({
                    ...EMPTY_ENGINE,
                    ...it,
                    kw: it?.kw == null ? "" : String(it.kw),
                    hours: it?.hours == null ? "" : String(it.hours),
                    serial_number: it?.serial_number || "",
                  }))
                : [],
            }))
          : [];
      } else {
        next[k] = v ?? "";
      }
    });
    setForm(next);
    setOpen(true);
  };

  // Normalise the engines/generators arrays for submission. Both kinds
  // share the same shape now (brand, type, serial_number, kw) with brand +
  // type required and the rest optional. Empty rows are dropped silently.
  const buildSpecArray = (rows, kind) => {
    const cleaned = [];
    rows.forEach((r) => {
      const brand = (r.brand || "").trim();
      const type = (r.type || "").trim();
      if (!brand && !type) return; // empty row → skip
      if (!brand || !type) {
        const noun = kind === "engines"
          ? "engine"
          : kind === "generators"
          ? "generator"
          : "stabilizer";
        throw new Error(`Each ${noun} needs a brand and a type.`);
      }
      const item = { brand, type };
      // Carry the stable engine/generator id through so the server doesn't
      // mint a new one on edit (which would break any routine schedule that
      // targets this engine by id).
      if (r.id) item.id = r.id;
      if (r.group && r.group.trim()) item.group = r.group.trim();
      if (r.serial_number && r.serial_number.trim()) item.serial_number = r.serial_number.trim();
      if (r.location && r.location.trim()) item.location = r.location.trim();
      if (r.kw !== "" && r.kw != null && !Number.isNaN(Number(r.kw))) item.kw = Number(r.kw);
      if (r.hours !== "" && r.hours != null && !Number.isNaN(Number(r.hours))) item.hours = Number(r.hours);
      cleaned.push(item);
    });
    return cleaned;
  };

  // Tenders carry their own identity + an engines[] sub-list. Each tender
  // needs a name; the rest is optional. Empty tenders (no name) are dropped.
  const buildTendersArray = (rows) => {
    const cleaned = [];
    rows.forEach((t) => {
      const name = (t.name || "").trim();
      if (!name) {
        // If the tender has any engines or other fields filled in, it's a
        // misuse — require a name explicitly. Otherwise skip silently.
        const hasOther =
          (t.brand || "").trim() || (t.model || "").trim() ||
          (t.length_m !== "" && t.length_m != null) ||
          (Array.isArray(t.engines) && t.engines.some((e) => e.brand || e.type));
        if (hasOther) throw new Error("Each tender needs a name.");
        return;
      }
      const item = { name };
      if (t.brand && t.brand.trim()) item.brand = t.brand.trim();
      if (t.model && t.model.trim()) item.model = t.model.trim();
      if (t.cover_image && t.cover_image.trim()) item.cover_image = t.cover_image.trim();
      if (t.length_m !== "" && t.length_m != null && !Number.isNaN(Number(t.length_m))) {
        item.length_m = Number(t.length_m);
      }
      item.engines = buildSpecArray(t.engines || [], "engines");
      cleaned.push(item);
    });
    return cleaned;
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.year) payload.year = Number(payload.year); else delete payload.year;
      if (payload.length_m) payload.length_m = Number(payload.length_m); else delete payload.length_m;
      payload.engines = buildSpecArray(form.engines, "engines");
      payload.generators = buildSpecArray(form.generators, "generators");
      payload.stabilizers = buildSpecArray(form.stabilizers, "stabilizers");
      payload.tenders = buildTendersArray(form.tenders);
      if (editingId) {
        // Send all fields that have meaningful content. Empty strings clear the
        // value on the server side; arrays are always sent (even empty) so the
        // user can remove all engines/generators if they want.
        const patch = {};
        Object.entries(payload).forEach(([k, v]) => {
          if (Array.isArray(v)) patch[k] = v;
          else if (v !== "" && v !== undefined) patch[k] = v;
        });
        const { data } = await api.patch(`/yachts/${editingId}`, patch);
        await refresh();
        if (activeYacht?.id === editingId && data) switchYacht(data);
        toast.success("Yacht updated");
      } else {
        const { data } = await api.post("/yachts", payload);
        await refresh();
        switchYacht(data);
        toast.success("Yacht created");
      }
      setOpen(false);
      setEditingId(null);
      setForm(DEFAULT);
    } catch (e) {
      toast.error(e?.message || fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Load pending delete requests for every yacht the user has
  const loadPending = async () => {
    if (!yachts?.length) {
      setPendingByYacht({});
      return;
    }
    const entries = await Promise.all(
      yachts.map(async (y) => {
        try {
          const { data } = await api.get(`/yachts/${y.id}/pending-delete`);
          return [y.id, data];
        } catch {
          return [y.id, null];
        }
      }),
    );
    setPendingByYacht(Object.fromEntries(entries.filter(([, v]) => v)));
  };
  useEffect(() => { loadPending(); /* eslint-disable-next-line */ }, [yachts]);

  // Ownership transfer state — dialog target, pending transfers by yacht.
  const [transferYacht, setTransferYacht] = useState(null);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferCrew, setTransferCrew] = useState(true);
  const [transferPettycash, setTransferPettycash] = useState(true);
  const [transferLogbook, setTransferLogbook] = useState(true);
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferByYacht, setTransferByYacht] = useState({});

  const loadTransfers = async () => {
    if (!(yachts || []).some((y) => ["owner", "manager"].includes(y.my_role))) {
      setTransferByYacht({});
      return;
    }
    try {
      const { data } = await api.get("/yacht-transfers/outgoing");
      setTransferByYacht(Object.fromEntries((data || []).map((t) => [t.yacht_id, t])));
    } catch {
      /* non-fatal */
    }
  };
  useEffect(() => { loadTransfers(); /* eslint-disable-next-line */ }, [yachts]);

  const submitTransfer = async () => {
    const email = transferEmail.trim().toLowerCase();
    if (!email || !transferYacht) return;
    setTransferBusy(true);
    try {
      const { data } = await api.post(`/yachts/${transferYacht.id}/transfer`, {
        email, include_crew: transferCrew,
        include_pettycash: transferPettycash, include_logbook: transferLogbook,
      });
      toast.success(
        data.owner_approved === false
          ? `Transfer request created — the yacht's owner must approve it before ${email} is contacted.`
          : data.new_user
            ? `Transfer request sent — ${email} will get an email to create their account and accept.`
            : `Transfer request sent to ${email} — they must accept before anything changes.`,
      );
      setTransferYacht(null);
      setTransferEmail("");
      loadTransfers();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setTransferBusy(false);
    }
  };

  const approveTransfer = async (yachtId) => {
    const t = transferByYacht[yachtId];
    if (!t) return;
    try {
      await api.post(`/yacht-transfers/${t.id}/approve`);
      toast.success("Transfer approved — the ownership offer has been sent to the recipient.");
      loadTransfers();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const cancelTransfer = async (yachtId) => {
    const t = transferByYacht[yachtId];
    if (!t) return;
    try {
      await api.post(`/yacht-transfers/${t.id}/cancel`);
      toast.info("Ownership transfer cancelled.");
      loadTransfers();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openDelete = (y) => {
    setDelTarget({ id: y.id, name: y.name });
    setDelStep("confirm");
    setDelMode(null);
    setDelRequestId(null);
    setDelApprover(null);
    setDelOtp("");
  };

  const closeDelete = () => {
    setDelTarget(null);
    setDelStep("confirm");
    setDelMode(null);
    setDelRequestId(null);
    setDelApprover(null);
    setDelOtp("");
    setDelBusy(false);
  };

  const requestDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      const { data } = await api.post(`/yachts/${delTarget.id}/request-delete`);
      setDelMode(data.mode);
      setDelRequestId(data.request_id);
      setDelApprover(data.approver || null);
      if (data.mode === "self_otp") {
        setDelStep("otp");
        toast.success("Verification code sent to your email");
      } else if (data.mode === "self_direct") {
        setDelStep("areYouSure");
      } else if (data.mode === "approver_link") {
        setDelStep("sentToApprover");
        toast.success(`Approval request sent to ${data.approver?.name || "the senior on this yacht"}`);
        loadPending();
      }
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDelBusy(false);
    }
  };

  const submitOtp = async () => {
    if (!delTarget || !delRequestId) return;
    if (delOtp.length < 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    // Move to final "are you sure" step. The actual delete fires there.
    setDelStep("areYouSure");
  };

  const finalDelete = async () => {
    if (!delTarget || !delRequestId) return;
    setDelBusy(true);
    try {
      const body = { request_id: delRequestId };
      if (delMode === "self_otp") body.otp = delOtp.trim();
      await api.post(`/yachts/${delTarget.id}/confirm-delete`, body);
      await refresh();
      toast.success(`${delTarget.name} deleted`);
      closeDelete();
    } catch (e) {
      toast.error(fmtErr(e));
      // If OTP got rejected, jump back to the OTP step
      if (delMode === "self_otp") setDelStep("otp");
    } finally {
      setDelBusy(false);
    }
  };

  const cancelPending = async (yachtId, yachtName) => {
    if (!confirm(`Cancel the pending deletion request for ${yachtName}?`)) return;
    try {
      await api.post(`/yachts/${yachtId}/cancel-delete`);
      toast.success("Request cancelled");
      loadPending();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={t("yachts.overline")}
        title={t("yachts.title")}
        right={
          canCreate ? (
            <div className="flex items-center gap-2">
              <TourReplayButton tourId="add-yacht" />
              <Button className="bg-ink hover:bg-black" data-testid="add-yacht-btn" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" /> {t("yachts.add_yacht")}
              </Button>
            </div>
          ) : null
        }
      />

      {/* Ownership-transfer banners — one per yacht where this user just
          became the new senior because the previous creator was removed. */}
      {notifs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="ownership-banners">
          {notifs.map((n) => (
            <div
              key={n.id}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
              data-testid={`ownership-banner-${n.yacht_id}`}
            >
              <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-white shrink-0">
                <Crown className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1 text-[13px] leading-snug text-emerald-900">
                You are now the <strong>{roleLabel(n.new_role)}</strong> on{" "}
                <strong>{n.yacht_name}</strong>.{" "}
                <span className="text-emerald-800/80">
                  {roleLabel(n.previous_owner_role)} {n.previous_owner_name} was
                  removed from the crew, so you've inherited the senior seat.
                </span>
              </div>
              <button
                type="button"
                onClick={() => dismissNotif(n)}
                className="text-emerald-700 hover:text-emerald-900 rounded p-1"
                data-testid={`dismiss-ownership-banner-${n.yacht_id}`}
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Shared create/edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(DEFAULT); } }}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[85vh] overflow-y-auto rounded-lg"
          style={{ maxHeight: "85dvh" }}
          data-testid="yacht-form-dialog"
          onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
          onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
          onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{editingId ? t("yachts.edit_yacht") : t("yachts.add_yacht")}</DialogTitle>
            {editingId && (
              <DialogDescription>
                Update this yacht's details. Crew and yacht-scoped data are not affected.
              </DialogDescription>
            )}
            {!editingId && (
              <DialogDescription>
                Choose your role on this yacht — Seavanto stores roles per-yacht, so you can be a
                captain on one and an owner on another.
              </DialogDescription>
            )}
          </DialogHeader>
          {!editingId && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-1" data-tour="yacht-role">
              {[
                ["owner", "Owner", "I own this yacht."],
                ["manager", "Manager", "I manage operations."],
                ["captain", "Captain", "I'm the captain."],
              ].map(([value, label, hint]) => {
                const active = form.your_role === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => set("your_role", value)}
                    className={`text-left rounded-md border px-3 py-2.5 transition-colors ${active ? "border-ink bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}
                    data-testid={`yacht-your-role-${value}`}
                  >
                    <div className="text-sm font-medium flex items-center justify-between">
                      {label}
                      {active && <span className="text-[10px] uppercase tracking-wider text-ink">selected</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500">{hint}</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Cover photo — shown at the top of the form so users see it
              before filling out the spec fields. */}
          <div data-tour="yacht-cover">
            <Label className="text-sm">Cover photo</Label>
            <ImageUpload
              value={form.cover_image}
              onChange={(v) => set("cover_image", v)}
              yachtId={editingId || undefined}
              folder="yacht-covers"
              testid="yacht-cover-upload"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour="yacht-basics">
            {[
              ["name", "Yacht name", true],
              ["brand", "Brand"],
              ["model", "Model"],
              ["year", "Year"],
              ["length_m", "Length (m)", false, "number"],
              ["flag", "Flag"],
              ["registration_no", "Registration #"],
              ["hin", "HIN (Hull Ident. Number)"],
              ["mmsi", "MMSI"],
              ["call_sign", "Call sign"],
              ["home_marina", "Home marina"],
              ["currency", "Currency"],
            ].map(([k, label, req, type]) => (
              <div key={k}>
                <Label htmlFor={k}>{label}{req && " *"}</Label>
                {k === "flag" ? (
                  <FlagPicker value={form.flag} onChange={(v) => set("flag", v)} />
                ) : k === "currency" ? (
                  <select
                    id={k}
                    className="h-9 w-full rounded-md border border-input px-2 text-sm bg-white"
                    value={form.currency || ""}
                    onChange={(e) => set("currency", e.target.value)}
                    data-testid="yacht-currency"
                  >
                    <option value="">—</option>
                    {["EUR", "USD", "GBP", "AED", "CHF", "AUD", "NZD", "SGD", "HKD", "SAR", "QAR"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                ) : k === "year" ? (
                  <YearPicker value={form.year} onChange={(v) => set("year", v)} />
                ) : k === "brand" ? (
                  <BrandPicker
                    value={form.brand}
                    onChange={(v) => set("brand", v)}
                    options={YACHT_BRANDS}
                    placeholder="Select or type brand…"
                    testid="yacht-brand"
                  />
                ) : k === "model" ? (
                  <TypePicker
                    brand={form.brand}
                    value={form.model}
                    catalog={YACHT_MODEL_CATALOG}
                    itemKey="model"
                    hintOf={(m) => `${m.length_m} m`}
                    secondOf={(m) => m.length_m}
                    onChange={(model, len) => {
                      if (len == null) { set("model", model); return; }
                      // Prefill typical machinery for the picked model — into
                      // empty sections OR sections still holding an earlier
                      // untouched prefill (so re-picking a model corrects them).
                      const counts = defaultSpecCounts(form.brand, model, len);
                      const patch = { model, length_m: String(len) };
                      const prefilled = [];
                      const replaceable = (key) => {
                        const rows = form[key] || [];
                        return !rows.length || JSON.stringify(rows) === prefillRef.current[key];
                      };
                      if (replaceable("engines")) {
                        const eng = typicalEngine(form.brand, model) || {};
                        patch.engines = counts.engines > 0
                          ? defaultLocationLayout(counts.engines).map((loc) => ({
                              ...EMPTY_ENGINE,
                              location: loc,
                              brand: eng.brand || "",
                              type: eng.type || "",
                              kw: eng.kw != null ? String(eng.kw) : "",
                              group: "Engines",
                            }))
                          : [];
                        prefillRef.current.engines = JSON.stringify(patch.engines);
                        if (counts.engines > 0) {
                          prefilled.push(eng.type
                            ? `${counts.engines} × ${eng.brand} ${eng.type} (${eng.kw} kW)`
                            : eng.brand
                              ? `${counts.engines} engine${counts.engines > 1 ? "s" : ""} (${eng.brand})`
                              : `${counts.engines} engine${counts.engines > 1 ? "s" : ""}`);
                        }
                      }
                      if (replaceable("generators")) {
                        patch.generators = counts.generators > 0
                          ? defaultLocationLayout(counts.generators).map((loc) => ({ ...EMPTY_GENERATOR, location: loc, group: "Generators" }))
                          : [];
                        prefillRef.current.generators = JSON.stringify(patch.generators);
                        if (counts.generators > 0) prefilled.push(`${counts.generators} generator${counts.generators > 1 ? "s" : ""}`);
                      }
                      if (replaceable("stabilizers")) {
                        patch.stabilizers = counts.stabilizers > 0
                          ? defaultLocationLayout(counts.stabilizers).map((loc) => ({ ...EMPTY_STABILIZER, location: loc, group: "Stabilizers" }))
                          : [];
                        prefillRef.current.stabilizers = JSON.stringify(patch.stabilizers);
                        if (counts.stabilizers > 0) prefilled.push(`${counts.stabilizers} stabilizer${counts.stabilizers > 1 ? "s" : ""}`);
                      }
                      setForm((f) => ({ ...f, ...patch }));
                      if (prefilled.length) {
                        toast.info(`Prefilled ${prefilled.join(", ")} — typical for this model. Adjust as needed.`);
                      }
                    }}
                    placeholder={form.brand ? `Select ${form.brand} model or type…` : "Select or type model…"}
                    testid="yacht-model"
                  />
                ) : (
                  <Input
                    id={k}
                    type={type || "text"}
                    value={form[k]}
                    required={!!req}
                    onChange={(e) => set(k, e.target.value)}
                    data-testid={`yacht-${k}`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Engines section */}
          <div data-tour="yacht-engines">
          <SpecSection
            label="Engines"
            items={form.engines}
            emptyItem={{ ...EMPTY_ENGINE, group: "Engines" }}
            onChange={(next) => set("engines", next)}
            fields={[
              { key: "location", label: "Name", kind: "select", options: ENGINE_LOCATIONS, placeholder: "—" },
              { key: "brand", label: "Brand *", required: true, kind: "brand", brandOptions: ENGINE_BRANDS, placeholder: "Select or type brand…" },
              { key: "type", label: "Type *", required: true, kind: "type", catalog: ENGINE_CATALOG, placeholder: "e.g. V12-1900" },
              { key: "kw", label: "Power (kW)", type: "number", placeholder: "auto" },
              { key: "hours", label: "Hours", type: "number", placeholder: "optional" },
              { key: "group", label: "Group", placeholder: "Engines" },
              { key: "serial_number", label: "Serial number", placeholder: "optional" },
            ]}
            addLabel="Add engine"
            testidPrefix="engine"
          />
          </div>

          {/* Generators section */}
          <div data-tour="yacht-generators">
          <SpecSection
            label="Generators"
            items={form.generators}
            emptyItem={{ ...EMPTY_GENERATOR, group: "Generators" }}
            onChange={(next) => set("generators", next)}
            fields={[
              { key: "location", label: "Name", kind: "select", options: ENGINE_LOCATIONS, placeholder: "—" },
              { key: "brand", label: "Brand *", required: true, kind: "brand", brandOptions: GENERATOR_BRANDS, placeholder: "Select or type brand…" },
              { key: "type", label: "Type *", required: true, kind: "type", catalog: GENERATOR_CATALOG, placeholder: "e.g. 27EFOZ" },
              { key: "kw", label: "Power (kW)", type: "number", placeholder: "auto" },
              { key: "hours", label: "Hours", type: "number", placeholder: "optional" },
              { key: "group", label: "Group", placeholder: "Generators" },
              { key: "serial_number", label: "Serial number", placeholder: "optional" },
            ]}
            addLabel="Add generator"
            testidPrefix="generator"
          />
          </div>

          {/* Tenders — each carries its own identity + engines sub-list. */}
          {/* Stabilizers — gyro or fin stabilizer units. Same brand/type
              auto-fill pattern as engines + generators. */}
          <SpecSection
            label="Stabilizers"
            items={form.stabilizers}
            emptyItem={{ ...EMPTY_STABILIZER, group: "Stabilizers" }}
            onChange={(next) => set("stabilizers", next)}
            fields={[
              { key: "brand", label: "Brand *", required: true, kind: "brand", brandOptions: STABILIZER_BRANDS, placeholder: "Select or type brand…" },
              { key: "type", label: "Type *", required: true, kind: "type", catalog: STABILIZER_CATALOG, placeholder: "e.g. Seakeeper 6" },
              { key: "kw", label: "Power (kW)", type: "number", placeholder: "auto" },
              { key: "hours", label: "Hours", type: "number", placeholder: "optional" },
              { key: "group", label: "Group", placeholder: "Stabilizers" },
              { key: "serial_number", label: "Serial number", placeholder: "optional" },
            ]}
            addLabel="Add stabilizer"
            testidPrefix="stabilizer"
          />

          {/* Tenders — listed below Stabilizers. */}
          <TendersSection
            tenders={form.tenders}
            onChange={(next) => set("tenders", next)}
          />

          <DialogFooter className="sm:justify-between gap-2">
            {/* Delete moved out of the yacht card into the Edit dialog so the
                card UI is cleaner. Only visible when editing an existing yacht
                and the caller is a senior who can request deletion. */}
            <div>
              {editingId && CAN_DELETE_ROLES.includes(activeYacht?.id === editingId ? activeYacht?.my_role : yachts.find((y) => y.id === editingId)?.my_role) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const y = yachts.find((yy) => yy.id === editingId);
                    if (y) { setOpen(false); openDelete(y); }
                  }}
                  className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-700"
                  data-testid="edit-yacht-delete"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete yacht
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy || !form.name || (!editingId && !form.your_role)} className="bg-ink hover:bg-black" data-testid="save-yacht">
                {busy ? "Saving…" : editingId ? "Save changes" : "Create yacht"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="yachts-grid">
        {yachts.length === 0 && (
          <div className="col-span-full rounded-lg border border-zinc-200 bg-white p-10 md:p-14 text-center" data-testid="yachts-empty-state">
            <div className="inline-grid place-items-center h-14 w-14 rounded-full bg-zinc-50 border border-zinc-200 mx-auto">
              <Ship className="h-7 w-7 text-zinc-500" />
            </div>
            <div className="font-display text-2xl md:text-3xl mt-5 tracking-tight">
              {canCreate ? "Welcome aboard." : "No yachts yet."}
            </div>
            <div className="text-sm text-zinc-600 mt-2 max-w-sm mx-auto">
              {canCreate
                ? "Create your first yacht in 30 seconds — we'll set it active automatically so every module is ready to go."
                : "Ask a yacht owner, manager or captain to invite you onto a yacht. You'll get an email with a sign-in link."}
            </div>
            {canCreate && (
              <Button
                className="bg-ink hover:bg-black mt-6"
                onClick={openCreate}
                data-testid="empty-state-add-yacht"
              >
                <Plus className="h-4 w-4 mr-2" /> Add your first yacht
              </Button>
            )}
          </div>
        )}
        {yachts.map((y) => {
          const pending = pendingByYacht[y.id];
          const pendingApproval = pending?.mode === "approver_link";  // hide delete only when waiting on someone else
          return (
            <div
              key={y.id}
              onClick={() => { switchYacht(y); navigate("/app/dashboard"); }}
              className="rounded-md border border-zinc-200 bg-white overflow-hidden cursor-pointer transition-colors hover:border-zinc-400 hover:shadow-sm"
              data-testid={`yacht-card-${y.id}`}
            >
              {/* Cover image: fixed 200px width, height auto to preserve the
                  uploaded photo's aspect ratio. The empty-state placeholder
                  keeps the same width so cards line up visually. */}
              <div className="p-4 pb-0">
                {y.cover_image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${y.cover_image}`}
                    alt={y.name}
                    className="rounded-md border border-zinc-200 block"
                    style={{ width: 200, height: "auto" }}
                    data-testid={`yacht-cover-${y.id}`}
                  />
                ) : (
                  <div
                    className="rounded-md border border-zinc-200 bg-zinc-50 grid place-items-center text-zinc-300"
                    style={{ width: 200, height: 125 }}
                  >
                    <Ship className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-medium leading-tight truncate">{y.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{[y.brand, y.model, y.length_m && `${y.length_m}m`].filter(Boolean).join(" · ")}</div>
                  </div>
                  <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full border border-zinc-200 uppercase tracking-wider">
                    {roleLabel(y.my_role)}
                  </span>
                </div>
                {y.my_role === "provider" && supSummary[y.id] && (
                  <div className="mt-2 space-y-0.5 text-[12px] text-zinc-600" data-testid={`yacht-supplier-summary-${y.id}`}>
                    {supSummary[y.id].leadership?.owner && (
                      <div className="text-zinc-500">Owner: <span className="text-zinc-800 font-medium" data-testid={`yacht-owner-${y.id}`}>{supSummary[y.id].leadership.owner}</span></div>
                    )}
                    {supSummary[y.id].leadership?.captain && (
                      <div className="text-zinc-500">Captain: <span className="text-zinc-800 font-medium" data-testid={`yacht-captain-${y.id}`}>{supSummary[y.id].leadership.captain}</span></div>
                    )}
                    {supSummary[y.id].contact?.name && (
                      <div className="text-zinc-500">Contact: <span className="text-zinc-800 font-medium">{supSummary[y.id].contact.name}</span></div>
                    )}
                    <div className="flex flex-wrap gap-x-4">
                      <span>Open tasks: <span className="font-medium text-zinc-900">{supSummary[y.id].open_tasks}</span></span>
                      <span>Open quote requests: <span className="font-medium text-zinc-900">{supSummary[y.id].open_quotes}</span></span>
                      <span>Awaiting decision: <span className="font-medium text-amber-700" data-testid={`yacht-supplier-awaiting-${y.id}`}>{supSummary[y.id].awaiting_decision ?? 0}</span></span>
                    </div>
                  </div>
                )}
                {pending?.mode === "approver_link" && (
                  <div
                    className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2"
                    data-testid={`pending-delete-${y.id}`}
                  >
                    <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">Deletion awaiting approval</div>
                      <div className="text-amber-800/80 truncate">
                        Sent to {pending.approver_name} ({pending.approver_role})
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); cancelPending(y.id, y.name); }}
                      className="text-amber-900 hover:text-amber-700 inline-flex items-center gap-1 text-[11px] underline"
                      data-testid={`cancel-pending-${y.id}`}
                    >
                      <XCircle className="h-3 w-3" /> Cancel
                    </button>
                  </div>
                )}
                {transferByYacht[y.id] && (
                  <div
                    className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2"
                    data-testid={`pending-transfer-${y.id}`}
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">
                        {transferByYacht[y.id].owner_approved === false
                          ? "Ownership transfer awaiting owner approval"
                          : "Ownership transfer pending"}
                      </div>
                      <div className="text-amber-800/80 truncate">
                        Sent to {transferByYacht[y.id].to_email}
                        {transferByYacht[y.id].include_crew === false && " · crew will be removed"}
                        {transferByYacht[y.id].include_pettycash === false && " · petty cash will be deleted"}
                        {transferByYacht[y.id].include_logbook === false && " · logbook will be deleted"}
                      </div>
                    </div>
                    {transferByYacht[y.id].owner_approved === false && y.my_role === "owner" && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); approveTransfer(y.id); }}
                        className="text-emerald-800 hover:text-emerald-600 inline-flex items-center gap-1 text-[11px] underline font-medium"
                        data-testid={`approve-transfer-${y.id}`}
                      >
                        <Check className="h-3 w-3" /> Approve
                      </button>
                    )}
                    {["owner", "manager"].includes(y.my_role) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); cancelTransfer(y.id); }}
                        className="text-amber-900 hover:text-amber-700 inline-flex items-center gap-1 text-[11px] underline"
                        data-testid={`cancel-transfer-${y.id}`}
                      >
                        <XCircle className="h-3 w-3" />
                        {transferByYacht[y.id].owner_approved === false && y.my_role === "owner" ? "Reject" : "Cancel"}
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setDetailsYacht(y); }}
                    data-testid={`details-yacht-${y.id}`}
                  >
                    <Ship className="h-3.5 w-3.5 mr-1.5" /> Details
                  </Button>
                  {CAN_DELETE_ROLES.includes(y.my_role) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openEdit(y); }}
                      data-testid={`edit-yacht-${y.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                    </Button>
                  )}
                  {["owner", "manager"].includes(y.my_role) && !transferByYacht[y.id] && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setTransferYacht(y); setTransferEmail(""); setTransferCrew(true); }}
                      data-testid={`transfer-yacht-${y.id}`}
                    >
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> Transfer
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Read-only Yacht details modal. Shows the full record + nested
          tenders / engines / generators with cover photos. Available to
          everyone with access to the yacht (no edit affordances here). */}
      <YachtDetailsDialog
        yacht={detailsYacht}
        onClose={() => setDetailsYacht(null)}
      />

      {/* Ownership transfer dialog — Owner/Manager offers the yacht to a new
          owner by email. Recipient must accept before anything changes. */}
      <Dialog open={!!transferYacht} onOpenChange={(o) => { if (!o) setTransferYacht(null); }}>
        <DialogContent className="max-w-md" data-testid="transfer-yacht-modal">
          <DialogHeader>
            <DialogTitle>Transfer ownership of {transferYacht?.name}</DialogTitle>
            <DialogDescription>
              Offer this yacht to a new owner. They'll receive an email and must accept
              before anything changes. If they don't have a Seavanto account yet, they'll
              be invited to create one.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-800">
            Once accepted, the new owner takes <strong>full control</strong> of this yacht and
            the current owner is <strong>removed from the yacht entirely</strong>. This cannot
            be undone.
          </div>
          <div>
            <Label>Is the crew transferring with the yacht?</Label>
            <div className="mt-1.5 space-y-2">
              <button
                type="button"
                onClick={() => setTransferCrew(true)}
                className={`w-full text-left rounded-md border px-3 py-2 text-[13px] transition-colors ${transferCrew ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
                data-testid="transfer-crew-yes"
              >
                <div className="font-medium">Yes — crew transfers too</div>
                <div className="text-zinc-500 text-[12px]">All crew members and their HR records stay on the yacht for the new owner.</div>
              </button>
              <button
                type="button"
                onClick={() => setTransferCrew(false)}
                className={`w-full text-left rounded-md border px-3 py-2 text-[13px] transition-colors ${!transferCrew ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
                data-testid="transfer-crew-no"
              >
                <div className="font-medium">No — remove the crew</div>
                <div className="text-zinc-500 text-[12px]">When the new owner accepts, all crew members and their HR records are removed. Suppliers (My Network) stay linked to the yacht.</div>
              </button>
            </div>
          </div>
          <div>
            <Label>What else transfers with the yacht?</Label>
            <div className="mt-1.5 space-y-2 text-[13px]">
              <label className="flex items-start gap-2.5 rounded-md border border-zinc-200 px-3 py-2 cursor-pointer hover:border-zinc-400" data-testid="transfer-pettycash-row">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-zinc-900" checked={transferPettycash}
                  onChange={(e) => setTransferPettycash(e.target.checked)} data-testid="transfer-pettycash-checkbox" />
                <span>
                  <span className="font-medium">Petty cash</span>
                  <span className="block text-zinc-500 text-[12px]">Unchecked: all petty cash records and settings are deleted when the new owner accepts.</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 rounded-md border border-zinc-200 px-3 py-2 cursor-pointer hover:border-zinc-400" data-testid="transfer-logbook-row">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-zinc-900" checked={transferLogbook}
                  onChange={(e) => setTransferLogbook(e.target.checked)} data-testid="transfer-logbook-checkbox" />
                <span>
                  <span className="font-medium">Logbook</span>
                  <span className="block text-zinc-500 text-[12px]">Unchecked: the entire logbook is deleted when the new owner accepts.</span>
                </span>
              </label>
            </div>
          </div>
          <div>
            <Label htmlFor="transfer-email">New owner's email</Label>
            <Input
              id="transfer-email"
              type="email"
              placeholder="new.owner@example.com"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              className="mt-1"
              data-testid="transfer-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferYacht(null)} data-testid="cancel-transfer-dialog">
              Cancel
            </Button>
            <Button
              onClick={submitTransfer}
              disabled={transferBusy || !transferEmail.includes("@")}
              className="bg-ink hover:bg-black"
              data-testid="submit-transfer"
            >
              {transferBusy ? "Sending…" : "Send transfer request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) closeDelete(); }}>
        <DialogContent className="max-w-md" data-testid="delete-yacht-modal">
          {delStep === "confirm" && (
            <>
              <DialogHeader>
                <DialogTitle>Delete {delTarget?.name}?</DialogTitle>
                <DialogDescription>
                  This permanently removes the yacht and all of its inventory, maintenance, tasks,
                  petty cash, logbook, documents and photos. Crew accounts are not deleted — only
                  their access to this yacht. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <p className="text-sm text-zinc-600">
                Click continue and we'll check whether your deletion requires approval from a
                senior on this yacht.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={closeDelete} data-testid="cancel-delete-yacht">
                  Cancel
                </Button>
                <Button
                  onClick={requestDelete}
                  disabled={delBusy}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="request-delete-yacht"
                >
                  {delBusy ? "Working…" : "Continue"}
                </Button>
              </DialogFooter>
            </>
          )}

          {delStep === "otp" && (
            <>
              <DialogHeader>
                <DialogTitle>Enter verification code</DialogTitle>
                <DialogDescription>
                  As the senior on this yacht, deletion requires a 6-digit code.
                  Check <strong>{user?.email}</strong> for the email we just sent.
                </DialogDescription>
              </DialogHeader>
              <div>
                <Label htmlFor="delete-otp">Verification code</Label>
                <Input
                  id="delete-otp"
                  inputMode="numeric"
                  maxLength={6}
                  value={delOtp}
                  onChange={(e) => setDelOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="tracking-[0.4em] text-center text-lg font-mono"
                  data-testid="delete-otp-input"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDelete} data-testid="cancel-delete-otp">
                  Cancel
                </Button>
                <Button
                  onClick={submitOtp}
                  disabled={delBusy || delOtp.length < 6}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="verify-delete-otp"
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}

          {delStep === "areYouSure" && (
            <>
              <DialogHeader>
                <DialogTitle>Are you absolutely sure?</DialogTitle>
                <DialogDescription>
                  This will permanently delete <strong>{delTarget?.name}</strong> and ALL of its
                  data — inventory, maintenance jobs, tasks, petty cash entries, logbook entries,
                  documents and photos. Crew accounts are preserved.
                </DialogDescription>
              </DialogHeader>
              <p className="text-sm font-medium text-red-700">
                There is no undo. Type-check the yacht name above before confirming.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={closeDelete} data-testid="cancel-final-delete">
                  No, keep it
                </Button>
                <Button
                  onClick={finalDelete}
                  disabled={delBusy}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-final-delete"
                >
                  {delBusy ? "Deleting…" : "Yes, delete permanently"}
                </Button>
              </DialogFooter>
            </>
          )}

          {delStep === "sentToApprover" && (
            <>
              <DialogHeader>
                <DialogTitle>Approval request sent</DialogTitle>
                <DialogDescription>
                  Your request to delete <strong>{delTarget?.name}</strong> has been emailed to{" "}
                  <strong>{delApprover?.name}</strong> ({delApprover?.role}). The yacht will only
                  be deleted once they approve the request from their email.
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 flex items-start gap-2">
                <MailCheck className="h-4 w-4 mt-0.5 text-emerald-600" />
                <div>
                  Sent to <span className="font-medium">{delApprover?.email}</span>. The approval
                  link expires in 24 hours. You'll be emailed if it isn't approved in time.
                </div>
              </div>
              <DialogFooter>
                <Button onClick={closeDelete} className="bg-ink hover:bg-black" data-testid="close-sent-modal">
                  Got it
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Repeatable rows of structured spec data (engines, generators, etc.).
 * Renders a "Add row" button + delete-per-row controls. The actual JSON
 * cleanup (drop empty rows, validate required, coerce kw) happens in
 * Yachts.save -> buildSpecArray.
 */
/**
 * Searchable country combobox with flag emojis. Stores the country name as
 * the value (e.g. "Greece") so the existing string-based `flag` field on
 * the yacht doc stays compatible.
 */
function FlagPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => countryByName(value), [value]);
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 font-normal"
          data-testid="yacht-flag"
        >
          {selected ? (
            <span className="inline-flex items-center gap-2 truncate">
              <span className="text-lg leading-none">{selected.flag}</span>
              <span className="truncate">{selected.name}</span>
            </span>
          ) : (
            <span className="text-zinc-500">Select country…</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country…" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(""); setOpen(false); }}
                  data-testid="flag-clear"
                  className="text-zinc-500"
                >
                  Clear selection
                </CommandItem>
              )}
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.code}`}
                  onSelect={() => { onChange(c.name); setOpen(false); }}
                  data-testid={`flag-${c.code}`}
                >
                  <span className="text-lg leading-none mr-2">{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                  {value === c.name && <Check className="h-4 w-4 text-emerald-600" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/**
 * Year dropdown — 1950 to next year, descending. Stores the value as a
 * string ("" when cleared); the parent serialiser coerces to int on submit.
 */
function YearPicker({ value, onChange }) {
  return (
    <Select
      value={value ? String(value) : ""}
      onValueChange={(v) => onChange(v === "__clear__" ? "" : v)}
    >
      <SelectTrigger className="h-9" data-testid="yacht-year">
        <SelectValue placeholder="Select year…" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {value && (
          <SelectItem value="__clear__" className="text-zinc-500">
            Clear selection
          </SelectItem>
        )}
        {YEAR_OPTIONS.map((y) => (
          <SelectItem key={y} value={String(y)} data-testid={`year-${y}`}>
            {y}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


/**
 * Combobox for brand fields — picks from a curated list with cmdk search,
 * but also lets the user type a custom brand when their option isn't in the
 * list. Custom entries surface as an "Use '…'" item at the top of the list.
 */
function BrandPicker({ value, onChange, options, placeholder = "Select brand…", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const exactMatch = options.some((b) => b.toLowerCase() === trimmed.toLowerCase());
  return (
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 font-normal"
          data-testid={testid}
        >
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-zinc-500 truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or type custom brand…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {trimmed && !exactMatch && (
                <CommandItem
                  value={`__custom_${trimmed}`}
                  onSelect={() => { onChange(trimmed); setOpen(false); setQuery(""); }}
                  data-testid={`${testid}-custom`}
                  className="text-zinc-700"
                >
                  Use "<span className="font-medium">{trimmed}</span>"
                </CommandItem>
              )}
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(""); setOpen(false); setQuery(""); }}
                  className="text-zinc-500"
                >
                  Clear selection
                </CommandItem>
              )}
              {options.map((b) => (
                <CommandItem
                  key={b}
                  value={b}
                  onSelect={() => { onChange(b); setOpen(false); setQuery(""); }}
                  data-testid={`${testid}-${b.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="flex-1">{b}</span>
                  {value === b && <Check className="h-4 w-4 text-emerald-600" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/**
 * Type/Model picker for engines & generators.
 *
 * Behaviour:
 *   - If the currently selected brand has a catalog entry, show its models
 *     (with kW hint) in the dropdown; selecting a model auto-fills the
 *     row's kw via onChange(typeVal, kwVal).
 *   - If the brand is unknown OR the user wants a custom type, they can
 *     type freely and the "Use '…'" item commits the custom value (kwVal = null).
 *   - If brand is empty, we still allow free text but no catalog suggestions.
 */
function TypePicker({ brand, value, onChange, catalog, placeholder = "Select or type model…", testid, itemKey = "type", hintOf = (m) => `${m.kw} kW`, secondOf = (m) => m.kw }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const models = brand && catalog[brand] ? catalog[brand] : [];
  const trimmed = query.trim();
  const exactMatch = models.some((m) => m[itemKey].toLowerCase() === trimmed.toLowerCase());
  return (
    <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 font-normal"
          data-testid={testid}
        >
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-zinc-500 truncate">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={models.length ? "Search model or type custom…" : "Type model…"}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {brand
                ? `No models in catalog for ${brand}. Type to add a custom model.`
                : "Select a brand first or type a custom model."}
            </CommandEmpty>
            <CommandGroup>
              {trimmed && !exactMatch && (
                <CommandItem
                  value={`__custom_${trimmed}`}
                  onSelect={() => { onChange(trimmed, null); setOpen(false); setQuery(""); }}
                  data-testid={`${testid}-custom`}
                  className="text-zinc-700"
                >
                  Use "<span className="font-medium">{trimmed}</span>"
                </CommandItem>
              )}
              {value && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange("", null); setOpen(false); setQuery(""); }}
                  className="text-zinc-500"
                >
                  Clear selection
                </CommandItem>
              )}
              {models.map((m) => (
                <CommandItem
                  key={m[itemKey]}
                  value={m[itemKey]}
                  onSelect={() => { onChange(m[itemKey], secondOf(m)); setOpen(false); setQuery(""); }}
                  data-testid={`${testid}-${m[itemKey].replace(/[\s/]+/g, "-").toLowerCase()}`}
                >
                  <span className="flex-1">{m[itemKey]}</span>
                  <span className="text-[11px] text-zinc-500 ml-2">{hintOf(m)}</span>
                  {value === m[itemKey] && <Check className="h-4 w-4 text-emerald-600 ml-2" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


function SpecSection({ label, items, emptyItem, onChange, fields, addLabel, testidPrefix }) {
  const add = () => onChange([...(items || []), { ...emptyItem }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const update = (idx, k, v) =>
    onChange(items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const updateMany = (idx, patch) =>
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  return (
    <div className="mt-2" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm">{label}</Label>
        <Button
          type="button"
          size="sm"
          className="bg-ink hover:bg-black"
          onClick={add}
          data-testid={`add-${testidPrefix}`}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> {addLabel}
        </Button>
      </div>
      {(!items || items.length === 0) ? (
        <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50/50 px-3 py-3 text-[12px] text-zinc-500">
          No {label.toLowerCase()} added yet.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((row, idx) => (
            <div
              key={idx}
              className="rounded-md border border-zinc-200 bg-white p-3 flex flex-col md:flex-row md:items-end gap-2"
              data-testid={`${testidPrefix}-row-${idx}`}
            >
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 flex-1">
                {fields.map((f) => (
                  <div key={f.key}>
                    <Label className="text-[11px] text-zinc-600">{f.label}</Label>
                    {f.kind === "brand" ? (
                      <BrandPicker
                        value={row[f.key] ?? ""}
                        onChange={(v) => {
                          // Atomically: set the new brand AND clear dependent
                          // fields (type + kw) so we never carry a model that
                          // doesn't belong to the new brand. Serial + hours
                          // are user-entered and unrelated to brand.
                          if (v !== row.brand) {
                            updateMany(idx, { brand: v, type: "", kw: "" });
                          } else {
                            update(idx, f.key, v);
                          }
                        }}
                        options={f.brandOptions}
                        placeholder={f.placeholder}
                        testid={`${testidPrefix}-${idx}-${f.key}`}
                      />
                    ) : f.kind === "type" ? (
                      <TypePicker
                        brand={row.brand}
                        value={row[f.key] ?? ""}
                        onChange={(typeVal, kwVal) => {
                          // Atomically: set type AND auto-fill kw when the
                          // catalog provided one. User can still override.
                          const patch = { type: typeVal };
                          if (kwVal != null) patch.kw = String(kwVal);
                          updateMany(idx, patch);
                        }}
                        catalog={f.catalog}
                        placeholder={f.placeholder}
                        testid={`${testidPrefix}-${idx}-${f.key}`}
                      />
                    ) : f.kind === "select" ? (
                      <select
                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                        value={row[f.key] ?? ""}
                        onChange={(e) => update(idx, f.key, e.target.value)}
                        data-testid={`${testidPrefix}-${idx}-${f.key}`}
                      >
                        <option value="">{f.placeholder || "—"}</option>
                        {(f.options || []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={f.type || "text"}
                        value={row[f.key] ?? ""}
                        placeholder={f.placeholder}
                        onChange={(e) => update(idx, f.key, e.target.value)}
                        data-testid={`${testidPrefix}-${idx}-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(idx)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`remove-${testidPrefix}-${idx}`}
                title={`Remove ${label.slice(0, -1).toLowerCase()}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Tenders section — each tender is a small craft with optional identity
 * (name + brand + model + length) and zero or more engines. Renders as a
 * stack of cards with an inline engines sub-list.
 */
function TendersSection({ tenders, onChange }) {
  const addTender = () => onChange([...(tenders || []), { ...EMPTY_TENDER, engines: [] }]);
  const removeTender = (idx) => onChange(tenders.filter((_, i) => i !== idx));
  const updateTender = (idx, patch) =>
    onChange(tenders.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

  return (
    <div className="mt-2" data-testid="tenders-section">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm">Tenders</Label>
        <Button
          type="button"
          size="sm"
          className="bg-ink hover:bg-black"
          onClick={addTender}
          data-testid="add-tender"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add tender
        </Button>
      </div>
      {(!tenders || tenders.length === 0) ? (
        <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50/50 px-3 py-3 text-[12px] text-zinc-500">
          No tenders added yet.
        </div>
      ) : (
        <div className="space-y-3">
          {tenders.map((t, idx) => (
            <div
              key={idx}
              className="rounded-md border border-zinc-200 bg-white p-3"
              data-testid={`tender-card-${idx}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  Tender {idx + 1}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTender(idx)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 -mt-1"
                  data-testid={`remove-tender-${idx}`}
                  title="Remove this tender"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <div>
                  <Label className="text-[11px] text-zinc-600">Name *</Label>
                  <Input
                    value={t.name ?? ""}
                    placeholder="e.g. Williams 345"
                    onChange={(e) => updateTender(idx, { name: e.target.value })}
                    data-testid={`tender-${idx}-name`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Brand</Label>
                  <BrandPicker
                    value={t.brand ?? ""}
                    onChange={(v) => updateTender(idx, { brand: v })}
                    options={TENDER_BRANDS}
                    placeholder="Select or type brand…"
                    testid={`tender-${idx}-brand`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Model</Label>
                  <TypePicker
                    brand={t.brand}
                    value={t.model ?? ""}
                    catalog={TENDER_MODEL_CATALOG}
                    itemKey="model"
                    hintOf={(m) => `${m.length_m} m`}
                    secondOf={(m) => m}
                    onChange={(model, entry) => {
                      const patch = { model };
                      if (entry && typeof entry === "object") {
                        patch.length_m = String(entry.length_m);
                        // Prefill the factory-standard engine(s), like the
                        // main yacht engines — only when none entered yet.
                        const eng = entry.engine;
                        if (eng && !(t.engines || []).length) {
                          const n = eng.engines || 1;
                          patch.engines = defaultLocationLayout(n).map((loc) => ({
                            ...EMPTY_ENGINE,
                            location: loc,
                            brand: eng.brand || "",
                            type: eng.type || "",
                            kw: eng.kw != null ? String(eng.kw) : "",
                            group: "Tenders",
                          }));
                          toast.info(`Prefilled ${n} × ${eng.brand} ${eng.type} (${eng.kw} kW) — typical for this tender. Adjust as needed.`);
                        }
                      }
                      updateTender(idx, patch);
                    }}
                    placeholder={t.brand ? `Select ${t.brand} model or type…` : "Select or type model…"}
                    testid={`tender-${idx}-model`}
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Length (m)</Label>
                  <Input
                    type="number"
                    value={t.length_m ?? ""}
                    placeholder="optional"
                    onChange={(e) => updateTender(idx, { length_m: e.target.value })}
                    data-testid={`tender-${idx}-length`}
                  />
                </div>
              </div>

              {/* Tender cover photo */}
              <div className="mb-3">
                <Label className="text-[11px] text-zinc-600">Cover photo</Label>
                <ImageUpload
                  value={t.cover_image || ""}
                  onChange={(v) => updateTender(idx, { cover_image: v })}
                  folder={`tender-covers/${idx}`}
                  testid={`tender-${idx}-cover-upload`}
                  aspect="aspect-[16/10]"
                />
              </div>

              {/* Each tender's engines — reuses SpecSection so the brand /
                  type pulldowns + auto-power behave identically to the main
                  yacht engines. */}
              <SpecSection
                label="Engines"
                items={t.engines || []}
                emptyItem={{ ...EMPTY_ENGINE, group: "Tenders" }}
                onChange={(nextEngines) => updateTender(idx, { engines: nextEngines })}
                fields={[
                  { key: "location", label: "Name", kind: "select", options: ENGINE_LOCATIONS, placeholder: "—" },
                  { key: "brand", label: "Brand *", required: true, kind: "brand", brandOptions: ENGINE_BRANDS, placeholder: "Select or type brand…" },
                  { key: "type", label: "Type *", required: true, kind: "type", catalog: ENGINE_CATALOG, placeholder: "e.g. F250" },
                  { key: "kw", label: "Power (kW)", type: "number", placeholder: "auto" },
                  { key: "hours", label: "Hours", type: "number", placeholder: "optional" },
                  { key: "group", label: "Group", placeholder: "Tenders" },
                  { key: "serial_number", label: "Serial number", placeholder: "optional" },
                ]}
                addLabel="Add engine"
                testidPrefix={`tender-${idx}-engine`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
